import React from "react";
import { requireAuth } from "utilities/auth";
import PageHeader from "components/PageLayout";
import { AlertTriangle, FileText } from "lucide-react";
import { PARTNER_AGENT_TERMS_VERSION } from "utilities/userType";

/**
 * Partner Agent Terms & Conditions.
 *
 * Placeholder scaffolding only — real language to be supplied by counsel.
 * The acceptance flow is handled by `PartnerAgentTermsModal`; this page
 * is the canonical read-only view any authenticated user can reference.
 */
function PartnerAgentTerms() {
  return (
    <div>
      <PageHeader
        title="Partner Agent Terms"
        subtitle="Terms governing partner users who create and manage client applications on Sound Commerce."
        meta={[{ label: "Version", value: String(PARTNER_AGENT_TERMS_VERSION) }]}
        icon={<FileText className="w-6 h-6" />}
      />

      <div className="container py-6 space-y-6">
        {/* Placeholder callout */}
        <div className="surface rounded-xl ring-1 ring-warning/30 bg-warning/5 p-5 flex gap-3">
          <div className="shrink-0 p-2 rounded-lg bg-warning/10 text-warning">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-warning uppercase tracking-wider">
              Placeholder — Not Final
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              This is draft language intended to scaffold the acceptance flow
              while legal counsel finalizes the definitive Partner Agent Terms.
              The final version will replace the content below before partners
              are required to re-acknowledge.
            </p>
          </div>
        </div>

        {/* Terms body */}
        <article className="surface rounded-xl ring-1 ring-border p-8">
          <div className="prose prose-invert max-w-none">
            <h2>1. Scope &amp; Purpose</h2>
            <p>
              These Partner Agent Terms (the <em>"Terms"</em>) govern your use
              of Sound Commerce&apos;s platform in your capacity as an agent
              acting on behalf of merchants (each a <em>"Client"</em>). By
              accepting these Terms you acknowledge that you are authorized to
              submit information about Clients to Sound Commerce for the
              purpose of payment-processor matching and onboarding support.
            </p>

            <h2>2. Accuracy of Submitted Information</h2>
            <p>
              You agree that all information you submit on behalf of a Client
              is, to the best of your knowledge, accurate and complete. You
              further acknowledge that Sound Commerce is a data intermediary
              and does not perform Know Your Customer, anti-money-laundering,
              or underwriting decisions itself — those determinations remain
              with the downstream processor.
            </p>

            <h2>3. Authority</h2>
            <p>
              You represent that you have obtained the Client&apos;s consent to
              share their business and ownership information with Sound
              Commerce and to nominate Sound Commerce to facilitate
              introductions to payment processors. You will retain a record of
              such consent for not less than two (2) years and will produce it
              upon Sound Commerce&apos;s reasonable request.
            </p>

            <h2>4. Confidentiality</h2>
            <p>
              You will handle Client information in accordance with applicable
              privacy laws and Sound Commerce&apos;s platform Terms of Service.
              You will not use information obtained through the platform for
              any purpose other than servicing the Client&apos;s payment
              operations needs.
            </p>

            <h2>5. No Guarantee of Approval</h2>
            <p>
              Sound Commerce makes no representation or warranty that any
              specific payment processor will approve a Client application.
              Approval decisions are made by the processor and governed by
              that processor&apos;s own documentation.
            </p>

            <h2>6. Record of Acceptance</h2>
            <p>
              When you accept these Terms, Sound Commerce records the date and
              time of your acceptance, the version of the Terms you accepted,
              your user-agent, and the IP address from which acceptance was
              submitted. This record is maintained as part of your user
              profile and may be produced to processors or other counterparties
              when reasonably required.
            </p>

            <h2>7. Termination</h2>
            <p>
              Sound Commerce may revoke your ability to act as a partner agent
              at any time. Upon revocation you will cease acting on behalf of
              Clients on the platform and, if requested, assist with the
              orderly transition of affected Clients.
            </p>

            <h2>8. Updates</h2>
            <p>
              Sound Commerce may update these Terms from time to time. Material
              updates increment the version number and will re-prompt you to
              re-accept before you may continue submitting Client data.
            </p>

            <h2>9. Contact</h2>
            <p>
              Questions regarding these Terms may be directed to{" "}
              <a href="mailto:legal@sound-commerce.com">
                legal@sound-commerce.com
              </a>
              .
            </p>

            <p className="text-sm text-muted-foreground">
              — End of placeholder text —
            </p>
          </div>
        </article>
      </div>
    </div>
  );
}

export default requireAuth(PartnerAgentTerms);
